import {
  Avatar,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  InputLabel,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Select,
  Typography
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import {
  Business as BusinessIcon,
  Cancel as CancelIcon,
  Person as PersonIcon,
  Save as SaveIcon,
  Settings as SettingsIcon,
  Visibility as VisibilityIcon
} from '@material-ui/icons';
import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import toastError from '../../errors/toastError';
import api from '../../services/api';

const useStyles = makeStyles((theme) => ({
  dialog: {
    '& .MuiDialog-paper': {
      minWidth: '900px',
      maxWidth: '1000px',
      maxHeight: '80vh'
    }
  },
  title: {
    backgroundColor: '#1a1a2e',
    color: 'white',
    padding: theme.spacing(2),
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1)
  },
  content: {
    backgroundColor: '#1a1a2e',
    color: 'white',
    padding: theme.spacing(2),
    maxHeight: '60vh',
    overflowY: 'auto'
  },
  actions: {
    backgroundColor: '#1a1a2e',
    color: 'white',
    padding: theme.spacing(2),
    gap: theme.spacing(1)
  },
  userItem: {
    backgroundColor: '#16213e',
    marginBottom: theme.spacing(1),
    borderRadius: 8,
    padding: theme.spacing(2),
    minHeight: '180px',
    alignItems: 'flex-start',
    '&:hover': {
      backgroundColor: '#1a4d7a'
    }
  },
  departmentItem: {
    backgroundColor: '#16213e',
    marginBottom: theme.spacing(1),
    borderRadius: 8,
    border: '1px solid #2a3f5f'
  },
  section: {
    marginBottom: theme.spacing(3)
  },
  sectionTitle: {
    color: '#4CAF50',
    fontWeight: 'bold',
    marginBottom: theme.spacing(2),
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1)
  },
  userAvatar: {
    backgroundColor: '#4CAF50',
    width: 32,
    height: 32
  },
  departmentChip: {
    margin: theme.spacing(0.25),
    backgroundColor: '#0f3460',
    color: 'white'
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: theme.spacing(4)
  },
  selectAll: {
    backgroundColor: '#2a3f5f',
    marginBottom: theme.spacing(1),
    borderRadius: 8,
    fontWeight: 'bold'
  }
}));

const DepartmentPermissionsModal = ({ open, onClose, onUpdate }) => {
  const classes = useStyles();
  const [users, setUsers] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [userPermissions, setUserPermissions] = useState({});

  useEffect(() => {
    if (open) {
      fetchData();
    }
  }, [open]);

  const fetchData = async () => {
    try {
      setLoading(true);
      
      // Buscar usuários com departamentos vinculados
      const { data: usersData } = await api.get('/users/list');
      const usersList = usersData || [];
      
      // Buscar departamentos
      const { data: departmentsData } = await api.get('/queue');
      
      setUsers(usersList);
      setDepartments(departmentsData);
      
      // Inicializar permissões com os dados atuais dos usuários
      const permissions = {};
      usersList.forEach(user => {
        permissions[user.id] = user.visibleDepartmentIds || [];
      });
      setUserPermissions(permissions);
      
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  };

  const handleUserPermissionChange = (userId, departmentIds) => {
    setUserPermissions(prev => ({
      ...prev,
      [userId]: departmentIds
    }));
  };

  const handleSelectAllDepartments = (userId) => {
    const currentUserPermissions = userPermissions[userId] || [];
    const allDepartmentIds = departments.map(dept => dept.id);
    
    if (currentUserPermissions.length === allDepartmentIds.length) {
      // Se todos estão selecionados, desselecionar todos
      handleUserPermissionChange(userId, []);
    } else {
      // Se nem todos estão selecionados, selecionar todos
      handleUserPermissionChange(userId, allDepartmentIds);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      
      // Atualizar apenas o campo visibleDepartmentIds de cada usuário
      // Preservar todos os outros dados do usuário
      const updatePromises = users.map(user => {
        const newPermissions = userPermissions[user.id] || [];
        
        // Verificar se houve mudança real nas permissões
        const currentPermissions = user.visibleDepartmentIds || [];
        const hasChanged = JSON.stringify(currentPermissions.sort()) !== JSON.stringify(newPermissions.sort());
        
        if (!hasChanged) {
          // Se não houve mudança, não fazer nenhuma requisição
          return Promise.resolve();
        }
        
        // Enviar apenas o campo que queremos atualizar
        return api.put(`/users/${user.id}`, {
          visibleDepartmentIds: newPermissions
        });
      });
      
      // Filtrar promessas vazias (usuários sem mudanças)
      const validPromises = updatePromises.filter(promise => promise);
      
      if (validPromises.length > 0) {
        await Promise.all(validPromises);
        toast.success('Permissões de departamentos atualizadas com sucesso!');
      } else {
        toast.info('Nenhuma alteração foi detectada.');
      }
      
      // Chamar callback de atualização se fornecido
      if (onUpdate) {
        onUpdate();
      }
      
      onClose();
      
    } catch (err) {
      toastError(err);
    } finally {
      setSaving(false);
    }
  };

  const renderDepartmentSelect = (user) => {
    const currentPermissions = userPermissions[user.id] || [];
    
    // Se é admin, mostrar mensagem informativa
    if (user.profile === 'admin') {
      return (
        <Box>
          <Chip
            icon={<VisibilityIcon />}
            label="Acesso Total (Administrador)"
            color="primary"
            size="small"
          />
          <Typography variant="caption" display="block" style={{ marginTop: 4, color: '#8892b0' }}>
            Administradores podem visualizar todos os departamentos
          </Typography>
        </Box>
      );
    }

    return (
      <Box>
        <FormControl variant="outlined" size="small" fullWidth>
          <InputLabel style={{ color: 'white' }}>Departamentos Visíveis no Painel</InputLabel>
          <Select
            multiple
            value={currentPermissions}
            onChange={(e) => handleUserPermissionChange(user.id, e.target.value)}
            label="Departamentos Visíveis no Painel"
            renderValue={(selected) => {
              if (selected.length === 0) return 'Nenhum selecionado';
              if (selected.length === departments.length) return 'Todos selecionados';
              return `${selected.length} selecionados`;
            }}
            style={{ 
              backgroundColor: '#0f3460',
              color: 'white'
            }}
          >
            <MenuItem
              value="ALL"
              onClick={() => handleSelectAllDepartments(user.id)}
              className={classes.selectAll}
            >
              <Checkbox
                checked={currentPermissions.length === departments.length}
                indeterminate={currentPermissions.length > 0 && currentPermissions.length < departments.length}
                style={{ color: 'white' }}
              />
              <ListItemText 
                primary="Selecionar Todos" 
                style={{ color: 'white' }}
              />
            </MenuItem>
            {departments.map((department) => (
              <MenuItem key={department.id} value={department.id}>
                <Checkbox 
                  checked={currentPermissions.includes(department.id)}
                  style={{ color: 'white' }}
                />
                <ListItemText 
                  primary={department.name} 
                  style={{ color: 'white' }}
                />
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        
        <Typography variant="caption" display="block" style={{ color: '#8892b0', marginTop: 8 }}>
          Selecione os departamentos que este usuário pode visualizar no Painel de Gestor
        </Typography>
      </Box>
    );
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth={false}
      className={classes.dialog}
    >
      <DialogTitle className={classes.title}>
        <SettingsIcon />
        <Typography variant="h6">
          Configurar Permissões de Departamentos - Painel Gestor
        </Typography>
      </DialogTitle>
      
      <DialogContent className={classes.content}>
        {loading ? (
          <Box className={classes.loadingContainer}>
            <CircularProgress />
            <Typography style={{ marginLeft: 16 }}>
              Carregando dados...
            </Typography>
          </Box>
        ) : (
          <>
            <Box className={classes.section}>
              <Typography className={classes.sectionTitle}>
                <PersonIcon />
                Usuários e Permissões de Departamentos
              </Typography>
              <Typography variant="body2" color="textSecondary" style={{ marginBottom: 16 }}>
                Configure quais departamentos cada usuário pode visualizar no Painel de Gestor.
                <br />
                <strong>Departamentos Selecionados para Visualização:</strong> Exibidos em azul na segunda linha abaixo do nome - departamentos que o usuário pode ver no Painel de Gestor.
                <br />
                <strong>Departamentos Vinculados (Trabalho):</strong> Exibidos em verde na terceira linha - departamentos onde o usuário pode trabalhar e receber tickets.
                <br />
                <strong>Nota:</strong> Administradores têm acesso automático a todos os departamentos. Usuários sem departamentos selecionados não poderão acessar o Painel de Gestor.
              </Typography>
              
              <List>
                {users.map((user) => (
                  <ListItem key={user.id} className={classes.userItem} alignItems="flex-start">
                    <ListItemIcon style={{ marginTop: 4 }}>
                      <Avatar className={classes.userAvatar}>
                        {user.name.charAt(0)}
                      </Avatar>
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Box>
                          {/* Primeira linha: Nome + Perfil */}
                          <Box display="flex" alignItems="center" gap={1} mb={1}>
                            <Typography style={{ color: 'white', fontWeight: 500 }}>
                              {user.name}
                            </Typography>
                            <Chip
                              label={user.profile}
                              size="small"
                              color={user.profile === 'admin' ? 'primary' : 'default'}
                            />
                          </Box>
                          
                          {/* Segunda linha: Box de Seleção de Departamentos - Logo abaixo do nome */}
                          <Box mb={2} maxWidth="800px">
                            {renderDepartmentSelect(user)}
                          </Box>

                          {/* Terceira linha: Departamentos Selecionados para Visualização */}
                          {(() => {
                            const currentPermissions = userPermissions[user.id] || [];
                            return (
                              <Box mb={1}>
                                <Typography variant="caption" style={{ color: '#64B5F6', fontWeight: 'bold', display: 'block' }}>
                                  Departamentos Selecionados para Visualização:
                                </Typography>
                                <Box mt={0.5} display="flex" flexWrap="wrap" gap={0.5}>
                                  {currentPermissions.length > 0 ? (
                                    currentPermissions.map(deptId => {
                                      const dept = departments.find(d => d.id === deptId);
                                      return dept ? (
                                        <Chip
                                          key={dept.id}
                                          label={dept.name}
                                          size="small"
                                          icon={<VisibilityIcon style={{ fontSize: 12 }} />}
                                          style={{ 
                                            backgroundColor: '#1976d2',
                                            color: 'white',
                                            fontSize: '11px',
                                            height: '24px',
                                            fontWeight: 'bold'
                                          }}
                                        />
                                      ) : null;
                                    })
                                  ) : (
                                    <Chip
                                      label="Nenhum departamento selecionado"
                                      size="small"
                                      style={{ 
                                        backgroundColor: '#666',
                                        color: '#ccc',
                                        fontSize: '11px',
                                        height: '24px'
                                      }}
                                    />
                                  )}
                                </Box>
                              </Box>
                            );
                          })()}

                          {/* Quarta linha: Departamentos Vinculados (se houver) */}
                          {user.queues && user.queues.length > 0 && (
                            <Box mb={1}>
                              <Typography variant="caption" style={{ color: '#4CAF50', fontWeight: 'bold', display: 'block' }}>
                                Departamentos Vinculados (Trabalho):
                              </Typography>
                              <Box mt={0.5} display="flex" flexWrap="wrap" gap={0.5}>
                                {user.queues.map(queue => (
                                  <Chip
                                    key={queue.id}
                                    label={queue.name}
                                    size="small"
                                    style={{ 
                                      backgroundColor: queue.color || '#4CAF50',
                                      color: 'white',
                                      fontSize: '11px',
                                      height: '24px',
                                      fontWeight: 'bold'
                                    }}
                                  />
                                ))}
                              </Box>
                            </Box>
                          )}
                        </Box>
                      }
                      secondary={
                        <Typography variant="body2" style={{ color: '#8892b0', marginTop: 4 }}>
                          {user.email}
                        </Typography>
                      }
                    />
                  </ListItem>
                ))}
              </List>
            </Box>

            <Divider style={{ backgroundColor: '#2a3f5f', margin: '24px 0' }} />

            <Box className={classes.section}>
              <Typography className={classes.sectionTitle}>
                <BusinessIcon />
                Departamentos Disponíveis
              </Typography>
              <Typography variant="body2" color="textSecondary" style={{ marginBottom: 16 }}>
                Lista de todos os departamentos que podem ser atribuídos aos usuários.
              </Typography>
              
              <List>
                {departments.map((department) => (
                  <ListItem key={department.id} className={classes.departmentItem}>
                    <ListItemIcon>
                      <Box
                        style={{
                          width: 24,
                          height: 24,
                          borderRadius: '50%',
                          backgroundColor: department.color || '#4CAF50'
                        }}
                      />
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Typography style={{ color: 'white', fontWeight: 500 }}>
                          {department.name}
                        </Typography>
                      }
                      secondary={
                        <Typography variant="body2" style={{ color: '#8892b0' }}>
                          ID: {department.id} • Cor: {department.color || 'Padrão'}
                        </Typography>
                      }
                    />
                  </ListItem>
                ))}
              </List>
            </Box>
          </>
        )}
      </DialogContent>
      
      <DialogActions className={classes.actions}>
        <Button
          onClick={onClose}
          disabled={saving}
          startIcon={<CancelIcon />}
          style={{ color: '#8892b0' }}
        >
          Cancelar
        </Button>
        <Button
          onClick={handleSave}
          disabled={loading || saving}
          startIcon={saving ? <CircularProgress size={16} /> : <SaveIcon />}
          variant="contained"
          color="primary"
        >
          {saving ? 'Salvando...' : 'Salvar Alterações'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DepartmentPermissionsModal;
